// @flow

// Libs
import React, { Component } from 'react'
import _size from 'lodash/size'
import _has from 'lodash/has'

// Types
import type { Option } from 'hfty/types'

// Components
import Textfield from 'react-mdc-web/lib/Textfield'
import Button from 'react-mdc-web/lib/Button'
import { Errors } from 'hfty-components-errors'
import Select from 'react-select'

// Utils
import traverse from 'fc-utils/traverse'

// Constants
import {
  PROP_FARM_NAME,
  PROP_EMAIL,
  PROP_COUNTRY,
  PROP_ADDRESS,
  PROP_POSTAL_CODE,
  PROP_CITY,
  PROP_PROVINCE,
  PROP_PHONE,
  PROP_MOBILE_PHONE,
  PROP_FAX,
  PROP_DEFAULT_UNITS,
} from 'hfty-model-billing-info'
import {
  COUNTRIES_OPTS,
  US_STATES_OPTS,
} from 'hfty'

// Messages
import messages from '../messages'

// Types
import type {
  UserOperation,
} from 'fc-operator-user/types'

// Styles
import { sidebar as sidebarCommonStyle, INDENT } from 'hfty-styles/src/common'
const sidebar = sidebarCommonStyle
const styles = {
  selectWrapper: {
    margin: '1em 0 .5em',
  },
  select: {
    letterSpacing: '.04em',
  },
  selectLabel: {
    display: 'inline-block',
    fontSize: '.75em',
    marginBottom: '.65em',
    color: 'var(--mdc-theme-text-hint-on-light,rgba(0,0,0,.87))',
  },
  buttonWrapper: {
    marginTop: INDENT,
  },
  successMsg: {
    backgroundColor: 'rgba(131, 181, 64, 0.3)',
    padding: 20,
    textAlign: 'center',
  },
  input: {
    width: 300,
  },
}

const getFarmLabel = traverse(['operation', 'model', PROP_FARM_NAME], { default: '' })
const getEmail = traverse(['operation', 'model', PROP_EMAIL], { default: '' })
const getCountry = traverse(['operation', 'model', PROP_COUNTRY], { default: 'United States' })
const getAddress = traverse(['operation', 'model', PROP_ADDRESS], { default: '' })
const getCity = traverse(['operation', 'model', PROP_CITY], { default: '' })
const getProvince = traverse(['operation', 'model', PROP_PROVINCE], { default: '' })
const getPostalCode = traverse(['operation', 'model', PROP_POSTAL_CODE], { default: '' })
const getPhone = traverse(['operation', 'model', PROP_PHONE], { default: '' })
const getMobilePhone = traverse(['operation', 'model', PROP_MOBILE_PHONE], { default: '' })
const getFax = traverse(['operation', 'model', PROP_FAX], { default: '' })

type Props = {
  operation: UserOperation,
  onChange: Function,
  onCancel: Function,
  onSubmit: Function,
}

export default class EditBillingInfo extends Component {
  props: Props
  onChange: Function
  onChangePassword: Function
  onSubmit: Function

  constructor (props: Props) {
    super(props)

    this.state = {
      countryMenuOpen: false,
    }

    this.onChangeFarmLabel = this.onChangeFarmLabel.bind(this)
    this.onChangeEmail = this.onChangeEmail.bind(this)
    this.onChangeCountry = this.onChangeCountry.bind(this)
    this.onChangeAddress = this.onChangeAddress.bind(this)
    this.onChangeCity = this.onChangeCity.bind(this)
    this.onChangeState = this.onChangeState.bind(this)
    this.onChangeZipCode = this.onChangeZipCode.bind(this)
    this.onChangePhone = this.onChangePhone.bind(this)
    this.onChangeMobilePhone = this.onChangeMobilePhone.bind(this)
    this.onChangeFax = this.onChangeFax.bind(this)
    this.onChangeDefaultUnits = this.onChangeDefaultUnits.bind(this)
    this.countryMenuClicked = this.countryMenuClicked.bind(this)

    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.isSubmitDisabled = this.isSubmitDisabled.bind(this)
  }

  onChangeFarmLabel (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_FARM_NAME]: e.currentTarget.value,
      },
    })
  }

  onChangeEmail (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_EMAIL]: e.currentTarget.value,
      },
    })
  }

  onChangeCountry (option: Option) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_COUNTRY]: option.value,
      },
    })
  }

  onChangeAddress (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_ADDRESS]: e.currentTarget.value,
      },
    })
  }

  onChangeCity (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_CITY]: e.currentTarget.value,
      },
    })
  }

  onChangeState (option: Option) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_PROVINCE]: option.value,
      },
    })
  }

  onChangeZipCode (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_POSTAL_CODE]: e.currentTarget.value,
      },
    })
  }

  onChangePhone (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_PHONE]: e.currentTarget.value,
      },
    })
  }

  onChangeMobilePhone (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_MOBILE_PHONE]: e.currentTarget.value,
      },
    })
  }

  onChangeFax (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_FAX]: e.currentTarget.value,
      },
    })
  }

  onChangeDefaultUnits (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_DEFAULT_UNITS]: e.currentTarget.value,
      },
    })
  }

  isSubmitDisabled (): boolean {
    const model = traverse(['operation', 'model'], { default: {} })(this.props)

    return !model || !model[PROP_FARM_NAME] || !model[PROP_EMAIL] || !model[PROP_ADDRESS] || !model[PROP_CITY] ||
     !model[PROP_PROVINCE] || !model[PROP_PHONE] || !model[PROP_POSTAL_CODE]
  }

  onSubmit (e: Event & { currentTarget: HTMLInputElement }) {
    e.preventDefault()
    this.props.onSubmit(this.props.operation)
  }

  onCancel (e: Event) {
    e.preventDefault()

    this.props.onCancel(this.props.operation)
  }

  countryMenuClicked (e: Event) {
    let open = this.state.countryMenuOpen
    this.setState({
      countryMenuOpen: !open,
    })
  }

  render (): ?React.Element<any> {
    if (!_size(this.props.operation)) {
      return null
    }
    let success = this.props.operation.success
    let hasErrors = !success && _size(this.props.operation.errors) ? this.props.operation.errors : false
    return (
      <form style={sidebar.container} onSubmit={this.onSubmit}>
        <Errors
          errors={!success ? this.props.operation.errors : null}
          messages={messages}
        />
        <Textfield
          id={`farmlabel-${this.props.operation.operationId}`}
          onChange={this.onChangeFarmLabel}
          value={getFarmLabel(this.props)}
          required
          floatingLabel={'Farm Name'}
          style={styles.input}
        />
        <Textfield
          id={`email-${this.props.operation.operationId}`}
          type='email'
          pattern='^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$'
          useInvalidProp
          invalid={_has(hasErrors, PROP_EMAIL)}
          helptextValidation
          helptext={messages[PROP_EMAIL].defaultMessage}
          onChange={this.onChangeEmail}
          value={getEmail(this.props)}
          required
          floatingLabel={'Email'}
          style={styles.input}
        />
        <div style={styles.selectWrapper}>
          <label
            style={styles.selectLabel}
            htmlFor={`country-${this.props.operation.operationId}`}>Country*</label>
          <Select
            clearable={false}
            name={`country-${this.props.operation.operationId}`}
            value={getCountry(this.props)}
            options={COUNTRIES_OPTS}
            style={styles.select}
            onChange={(option: Option) => {
              this.onChangeCountry(option)
            }}
          />
        </div>
        <Textfield
          id={`address-${this.props.operation.operationId}`}
          onChange={this.onChangeAddress}
          value={getAddress(this.props)}
          required
          floatingLabel={'Address'}
          style={styles.input}
        />
        <Textfield
          id={`city-${this.props.operation.operationId}`}
          onChange={this.onChangeCity}
          value={getCity(this.props)}
          required
          floatingLabel={'City'}
          style={styles.input}
        />
        <div style={styles.selectWrapper}>
          <label
            style={styles.selectLabel}
            htmlFor={`state-${this.props.operation.operationId}`}>State*</label>
          <Select
            clearable={false}
            name={`state-${this.props.operation.operationId}`}
            value={getProvince(this.props)}
            options={US_STATES_OPTS}
            style={styles.select}
            onChange={(option: Option) => {
              this.onChangeState(option)
            }}
          />
        </div>
        <Textfield
          id={`postalCode-${this.props.operation.operationId}`}
          onChange={this.onChangeZipCode}
          value={getPostalCode(this.props)}
          minLength={5}
          maxLength={9}
          required
          floatingLabel={'Zip Code'}
          // useInvalidProp //0.14 unfortunately does not support this
          // invalid={_has(hasErrors, PROP_POSTAL_CODE)}
          helptextValidation
          helptext={messages[PROP_POSTAL_CODE].defaultMessage}
          style={styles.input}
        />
        <Textfield
          id={`phone-${this.props.operation.operationId}`}
          type='number'
          onChange={this.onChangePhone}
          value={getPhone(this.props)}
          required
          floatingLabel={'Phone'}
          style={styles.input}
        />
        <Textfield
          id={`mobilePhone-${this.props.operation.operationId}`}
          type='number'
          onChange={this.onChangeMobilePhone}
          value={getMobilePhone(this.props)}
          floatingLabel={'Mobile Phone'}
          style={styles.input}
        />
        <Textfield
          id={`fax-${this.props.operation.operationId}`}
          type='number'
          onChange={this.onChangeFax}
          value={getFax(this.props)}
          floatingLabel={'Fax'}
          style={styles.input}
        />
        <div style={{...sidebar.buttonWrapper, ...styles.buttonWrapper}}>
          <Button
            raised
            primary
            disabled={this.isSubmitDisabled()}
            style={sidebar.button}
            onClick={this.onSubmit}
          >
            {'Submit'}
          </Button>
          <Button
            raised
            onClick={this.onCancel}
          >
            {'Cancel'}
          </Button>
        </div>
      </form>
    )
  }
}
